import {ColumnDef, IdModelTable} from "../../../components/ExTable";
import {useTranslation} from "react-i18next";
import {IdListRequest, useOrganizationEetsDeviceListQuery} from "../../../app/api/api";
import React from "react";
import {EetsDevice, EetsDeviceField} from "../../../app/api/model/eetsDevice";
import {dateTimeColumnDef, defaultColumnDef} from "../../../components/ExTable/variables";
import {Tooltip, Typography} from "@mui/material";
import {EMPTY_UI} from "../../../variables";
import {getStatusLabel} from "../../orders/helpers";
import {ordinaryDetailPageLinkTo} from "../../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../../types";

export default function EetsDeviceList({organizationId}: {organizationId: number}) {
    const {t} = useTranslation();

    const columnDefs: ColumnDef<EetsDevice>[] = [
        {
            fieldName: EetsDeviceField.ID,
            label: t("generic_id"),
            width: 80,
            generateLink: ordinaryDetailPageLinkTo(
                `${RoutePath.ORGANIZATIONS}/${organizationId}/${RoutePath.EETS_DEVICES}`
            ),
            sticky: true,
            ...defaultColumnDef,
        },
        {
            fieldName: EetsDeviceField.DKV_CUSTOMER_NUMBER,
            label: t("organizations_dkvCustomerNumber"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.LEO_ID,
            label: t("eets_leoId"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.LEO_ART,
            label: t("eets_leoArticle"),
            sortable: false,
            formatCellText: ({cellValue}) =>
                cellValue
                    ? {
                          title: cellValue?.description || "no article",
                          node: (
                              <Tooltip title={cellValue?.description || "no article"}>
                                  <Typography variant={"body1"}>{cellValue?.code ?? EMPTY_UI}</Typography>
                              </Tooltip>
                          ),
                      }
                    : EMPTY_UI,
        },
        {
            fieldName: EetsDeviceField.OBU_ID,
            label: t("eets_obuId"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.T4E_ID,
            label: t("eets_t4eContractId"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.VIN,
            label: t("eets_vin"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.LICENSE_PLATE,
            label: t("eets_licensePlate"),
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.CANCELED_AT,
            label: t("eetsDevice_canceledAt"),
            ...dateTimeColumnDef,
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.CANCEL_REASON,
            label: t("generic_cancelReason"),
            sortable: false,
            formatCellText: ({cellValue}) => cellValue?.description || EMPTY_UI,
        },
        {
            fieldName: EetsDeviceField.LOCKED_AT,
            label: t("generic_lockedAt"),
            ...dateTimeColumnDef,
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.UNLOCKED_AT,
            label: t("generic_unlockedAt"),
            ...dateTimeColumnDef,
            sortable: false,
        },
        {
            fieldName: EetsDeviceField.LOCK_REASON,
            label: t("eets_lockReason"),
            sortable: false,
            formatCellText: ({cellValue}) => cellValue?.description || EMPTY_UI,
        },
        {
            fieldName: EetsDeviceField.STATUS,
            label: t("generic_status"),
            sortable: false,
            formatCellText: ({cellValue}) => {
                return getStatusLabel(cellValue, t);
            },
        },
    ];

    return (
        <>
            <IdModelTable<EetsDevice, IdListRequest>
                queryHook={useOrganizationEetsDeviceListQuery}
                additionalRequestParams={{id: organizationId}}
                columnDefs={columnDefs}
                title={t("organizations_eetsDevices")}
            />
        </>
    );
}
